'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { useEffect, useState } from 'react';
import { FaFacebook } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { IoIosLink } from 'react-icons/io';

interface BlogHeaderProps {
  title: string;
  locale: string;
  blog?: Blog;
}

export function BlogShare({ blog, locale }: BlogHeaderProps) {
  const { t } = useTranslations();
  const isCzech = locale.startsWith('cs');

  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);
    }
  }, []);

  let formattedDate;

  try {
    if (blog) {
      formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
    }
  } catch (error) {
    if (error instanceof RangeError && blog) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  const socialsToShare = [
    {
      name: 'Facebook',
      label: isCzech ? 'Sdílet článek na Facebooku' : 'Zdieľať článok na Facebooku',
      icon: <FaFacebook size={20} className="text-foreground" />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: 'X',
      label: isCzech ? 'Sdílet článek na X' : 'Zdieľať článok na X',
      icon: <FaXTwitter size={20} className="text-foreground" />,
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: 'Copy',
      label: isCzech ? 'Kopírovat odkaz na článek' : 'Kopírovať odkaz na článok',
      icon: <IoIosLink size={20} className="text-foreground" />,
      onClick: () => {
        navigator.clipboard.writeText(currentUrl);
        alert('URL copied to clipboard!');
      },
    },
  ];

  return (
    <div className="mt-4 flex w-full flex-col items-start justify-start gap-6 sm:flex-row sm:items-center sm:justify-start">
      <p className="text-sm font-normal text-foreground">{t('text1155')}</p>
      <div className="flex items-center gap-2.5">
        {socialsToShare.map(social => (
          <ShareButton social={social} key={social.name} />
        ))}
      </div>
    </div>
  );
}

function ShareButton({
  social,
}: {
  social: {
    name: string;
    label?: string;
    icon: React.ReactNode;
    url?: string;
    onClick?: () => void;
  };
}) {
  return social.url ? (
    <Link
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={social.label || social.name}
      className={cn(
        'flex min-h-10 min-w-10 items-center justify-center rounded-full bg-[#f5f5f5] transition-all',
      )}
    >
      {social.icon}
    </Link>
  ) : (
    <button
      type="button"
      data-ea-visual-tag="div"
      onClick={social.onClick}
      className={cn(
        'flex min-h-10 min-w-10 items-center justify-center rounded-full bg-[#f5f5f5] transition-all',
      )}
      aria-label={social.label || social.name}
    >
      {social.icon}
    </button>
  );
}
